import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getUpdateChargePointDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.form-default-values';
import { UpdateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdateChargePointMutation } from '@service/cpms-bo-charge-point.service';

import {
  UpdateChargePointContextProvider,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointUpdateFormController } from './charge-point-update-form/charge-point-update-form.controller';

interface ChargePointUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointUpdateController = ({
  onCancel,
  onSuccess,
}: ChargePointUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateChargePointMutation] = useCpmsBoUpdateChargePointMutation();
  const chargePoint = useChargePointContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-update.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.chargePoint) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    const updateChargePoint = {
      chargePointId: fieldValues.chargePointId,
      chargePoint: sanitizedObject.chargePoint,
    };

    updateChargePointMutation(updateChargePoint)
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.charge-point-update.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getUpdateChargePointDefaultValues(chargePoint);

  return (
    <UpdateChargePointContextProvider data={data}>
      <ChargePointUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateChargePointContextProvider>
  );
};
